import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
    AppScreen, Button, ButtonFooter, PageIntro, Section, SectionHeader,
} from 'components/ds';
import styles from './TimePicker.module.css';

/* 인라인 SVG — 이모지 금지 */
const IconSun = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
);

const IconMoon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
);

const IconMinus = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);

const IconPlus = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);

export const TimePicker = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const location = useLocation();
    const [period, setPeriod] = useState<'오전' | '오후'>('오후');
    const [hour, setHour] = useState(6);
    const [minute, setMinute] = useState(0);

    // 기존 선택값이 state 로 들어오면 초기값 반영
    useEffect(() => {
        const prev = (location.state as any)?.selectedTime as string | undefined;
        if (!prev) return;
        const m = prev.match(/(오전|오후)\s*(\d+):(\d+)/);
        if (!m) return;
        setPeriod(m[1] === '오전' ? '오전' : '오후');
        setHour(parseInt(m[2]));
        const mm = parseInt(m[3]);
        setMinute(mm - (mm % 5)); // 5분 단위로 snap
    }, [location.state]);

    const handleConfirm = () => {
        const selectedTime = `${period} ${hour}:${minute.toString().padStart(2, '0')}`;
        const returnTo = (location.state as any)?.returnTo || '/meeting/create/step2';
        navigate(returnTo, {
            state: {
                ...(location.state as any),
                selectedTime,
            },
            replace: true,
        });
    };

    // 시간 증감 (1시간 단위, 12까지 순환)
    const bumpHour = (delta: number) => {
        setHour(prev => {
            let next = prev + delta;
            if (next > 12) next = 1;
            if (next < 1) next = 12;
            return next;
        });
    };

    // 분 증감 (5분 단위, 0-55 순환)
    const bumpMinute = (delta: number) => {
        setMinute(prev => {
            let next = prev + delta;
            if (next >= 60) next = 0;
            if (next < 0) next = 55;
            return next;
        });
    };

    const minuteOptions = [0, 10, 20, 30, 40, 50];

    return (
        <AppScreen title={t('createFlow.selectTime')} bar="sub" hasFooter>
            <PageIntro title={t('createFlow.timeHero')} desc={t('createFlow.timeHeroSub')} />

            {/* 오전 · 오후 */}
            <Section spacing="tight">
                <div className={styles.period}>
                    {(['오전', '오후'] as const).map(p => (
                        <button
                            key={p}
                            type="button"
                            className={period === p ? `${styles.periodBtn} ${styles.periodBtnOn}` : styles.periodBtn}
                            onClick={() => setPeriod(p)}
                            aria-pressed={period === p}
                        >
                            {p === '오전' ? <IconSun /> : <IconMoon />}
                            {p === '오전' ? t('scheduleInput.am') : t('scheduleInput.pm')}
                        </button>
                    ))}
                </div>
            </Section>

            {/* 시 · 분 */}
            <Section divided>
                <div className={styles.steppers}>
                    <div className={styles.stepper}>
                        <button
                            type="button"
                            className={styles.stepBtn}
                            onClick={() => bumpHour(-1)}
                            aria-label={t('createFlow.hour')}
                        >
                            <IconMinus />
                        </button>
                        <span className={styles.stepValue}>
                            <span className={styles.stepNum}>{hour}</span>
                            <span className={styles.stepUnit}>{t('createFlow.hour')}</span>
                        </span>
                        <button
                            type="button"
                            className={styles.stepBtn}
                            onClick={() => bumpHour(1)}
                            aria-label={t('createFlow.hour')}
                        >
                            <IconPlus />
                        </button>
                    </div>

                    <span className={styles.colon} aria-hidden>:</span>

                    <div className={styles.stepper}>
                        <button
                            type="button"
                            className={styles.stepBtn}
                            onClick={() => bumpMinute(-5)}
                            aria-label={t('createFlow.minute')}
                        >
                            <IconMinus />
                        </button>
                        <span className={styles.stepValue}>
                            <span className={styles.stepNum}>{minute.toString().padStart(2, '0')}</span>
                            <span className={styles.stepUnit}>{t('createFlow.minute')}</span>
                        </span>
                        <button
                            type="button"
                            className={styles.stepBtn}
                            onClick={() => bumpMinute(5)}
                            aria-label={t('createFlow.minute')}
                        >
                            <IconPlus />
                        </button>
                    </div>
                </div>
            </Section>

            {/* 분 빠르게 */}
            <Section divided>
                <SectionHeader title={t('createFlow.quickSelect')} />
                <div className={styles.chips}>
                    {minuteOptions.map(m => (
                        <button
                            key={m}
                            type="button"
                            className={minute === m ? `${styles.chip} ${styles.chipOn}` : styles.chip}
                            onClick={() => setMinute(m)}
                            aria-pressed={minute === m}
                        >
                            {m.toString().padStart(2, '0')}
                        </button>
                    ))}
                </div>
            </Section>

            <Section divided last>
                <div className={styles.preview}>
                    <span className={styles.previewLabel}>{t('createFlow.selectedTimeLabel')}</span>
                    <span className={styles.previewValue}>
                        {period === '오전' ? t('scheduleInput.am') : t('scheduleInput.pm')}{' '}
                        {hour}:{minute.toString().padStart(2, '0')}
                    </span>
                </div>
            </Section>

            <ButtonFooter>
                <Button variant="primary" size="lg" block onClick={handleConfirm}>
                    {t('createFlow.pickThisTime')}
                </Button>
            </ButtonFooter>
        </AppScreen>
    );
};
